/**
 * C-GTCC-VIEW-HW-GW2 — GTCC view "Hardware & SIMs" (`hw`), 1:1 after
 * `docs/design/GTCC_REFERENCE_2026-09-01.html`, `div[data-v="hw"]`
 * (lines 736-770), reference frames
 * `docs/design/gtcc-screens/_reference/hw-{dark,light}.png`.
 *
 * Layout of the reference, element for element:
 *   - head "Hardware & SIMs" with the `.fresh` badge and the `.sub` line of
 *     line 739 (its trailing "Beispieldaten." is dropped — this view measures),
 *   - the four `.tab`s at `font-size:12px`, "Freigaben (Device Approval)"
 *     active,
 *   - panel "Freigabe-Warteschlange" with the five-column queue table,
 *   - the `.tiles` strip: TRACKER GESAMT, STILL > 24 H, WARTEN AUF FREIGABE,
 *     SIM AKTIV,
 *   - `.grid-main`: "Auffällige Geräte" and "Managed Hardware (Lager)".
 *
 * The three tabs the reference draws but does not fill re-cut the same measured
 * sources: "Inventar" the tracker rows of the vehicle-flow projection, "SIMs"
 * the SIM rows of the inventory projection, "Managed Hardware" the pool list.
 * No tab is inert and none shows a number that is not measured somewhere.
 *
 * Sources. Four of the five reads already existed for the old console; only
 * `/v1/admin/gtcc/hw/**` is new, and both of its routes are read-only
 * (`services/api/src/modules/gtcc/hw/`). The two verbs in the AKTION cell are
 * the established device-approval routes — this ticket adds no write endpoint
 * and no migration:
 *   `GET /v1/admin/devices/pending`, `GET /v1/admin/gtcc/ops/vehicle-flow`,
 *   `GET /v1/admin/gtcc/hw/inventory`, `GET /v1/admin/gtcc/hw/flagged-devices`,
 *   `GET /v1/admin/managed-hardware/pool`,
 *   `POST /v1/admin/devices/:id/approve`, `POST /v1/admin/devices/:id/reject`.
 *
 * Nothing here derives an age from the local clock, and no field is wired to a
 * constant "nicht gemessen": every unmeasurable cell is unmeasurable because
 * the payload says so and names its reason (P8/P9/P10).
 */
import {
  Button,
  ErrorState,
  LoadingState,
  Modal,
  Stack,
  Tabs,
  TextInput,
  type TabItem,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type ReactNode } from "react";

import {
  HW_FLAGGED_DEVICES_KEY,
  HW_INVENTORY_KEY,
  HW_PENDING_KEY,
  HW_POOL_KEY,
  HW_REJECT_REASON_MAX,
  HW_VEHICLE_FLOW_KEY,
  approveHwDevice,
  fetchHwFlaggedDevices,
  fetchHwInventory,
  fetchHwPendingDevices,
  fetchHwPool,
  fetchHwVehicleFlow,
  rejectHwDevice,
  type GtccResult,
  type HwDecisionDto,
  type HwFlaggedDevicesDto,
  type HwInventoryDto,
  type HwPendingDeviceDto,
  type HwPendingDevicesDto,
  type HwPoolDto,
  type HwVehicleFlowDto,
} from "../../api/hw.js";
import {
  ControlFreshBadge,
  ControlHead,
  GRID_COLUMN_STYLE,
  GRID_MAIN_CLASS,
  asList,
  formatInteger,
  maskImei,
  measuredNumber,
} from "../control/ControlPrimitives.js";
import {
  HW_QUERY_OPTIONS,
  HW_SUB_STYLE,
  HW_TAB_IDS,
  HwApprovalQueuePanel,
  HwFlaggedPanel,
  HwRowsPanel,
  HwStockPanel,
  HwTiles,
  TAB_BAR_STYLE,
  type HwTabId,
} from "./components/HwPanels.js";
import { HardwareSection, SimsSection } from "./GtccHardwareView.js";

/* ------------------------------------------------------------------ tabs */

const DEFAULT_HW_TAB: HwTabId = "approvals";
const TAB_ID_PREFIX = "gtcc-hw";

function isHwTabId(value: string): value is HwTabId {
  return (HW_TAB_IDS as ReadonlyArray<string>).includes(value);
}

/** Mirrors `initialTabFromQuery()` of `TenView.tsx:58` — one deep-link grammar. */
function initialTabFromQuery(): HwTabId {
  if (typeof window === "undefined") return DEFAULT_HW_TAB;
  const requested = new URLSearchParams(window.location.search).get("tab");
  if (requested !== null && isHwTabId(requested)) return requested;
  return DEFAULT_HW_TAB;
}

function writeQuery(tab: HwTabId): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("tab", tab);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

/* --------------------------------------------------------------- helpers */

function dataOf<T>(query: UseQueryResult<GtccResult<T>>): T | null {
  return query.data !== undefined && query.data.ok ? query.data.data : null;
}

/**
 * The badge shows the OLDEST of the freshnesses the payloads carry, because a
 * head that shows the youngest would call the whole view fresh while half of it
 * is stale. Same rule as `OvwView.tsx`.
 */
function oldestFreshness(
  sources: ReadonlyArray<{ readonly freshnessSeconds?: number | null } | null>,
): number | null {
  const measured = sources
    .map((source) => measuredNumber(source?.freshnessSeconds))
    .filter((value): value is number => value !== null);
  return measured.length === 0 ? null : Math.max(...measured);
}

/* --------------------------------------------------------- reject dialog */

interface RejectDraft {
  readonly deviceId: string;
  readonly tenantId: string;
  readonly imei: string;
}

/**
 * "Ablehnen" of the reference is a static mono word; the route behind it takes a
 * mandatory reason of 1..{@link HW_REJECT_REASON_MAX} characters, so the word
 * opens the smallest dialog that can satisfy it — the same shape `AlinView`
 * uses for its acknowledging silence. A rejection without a stated reason is
 * not offered at all.
 */
function HwRejectDialog(props: {
  draft: RejectDraft | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}): ReactNode {
  const { t } = useLocale();
  const [reason, setReason] = useState("");

  const { draft } = props;
  if (draft === null) return null;

  const trimmed = reason.trim();
  const valid = trimmed.length > 0 && trimmed.length <= HW_REJECT_REASON_MAX;

  return (
    <Modal
      open
      onClose={props.onClose}
      dismissible={!props.pending}
      title={t("admin.gtcc.hw.reject.dialog.title")}
      dataTestId="gtcc-hw-reject-dialog"
    >
      <Stack gap={3}>
        <p style={HW_SUB_STYLE} data-testid="gtcc-hw-reject-dialog-device">
          {maskImei(draft.imei)}
        </p>
        <TextInput
          label={t("admin.gtcc.hw.reject.dialog.reason")}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          hint={t("admin.gtcc.hw.reject.dialog.reason_hint", { max: HW_REJECT_REASON_MAX })}
          data-testid="gtcc-hw-reject-reason"
        />
        {props.error === null ? null : (
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.hw.reject.dialog.failed")}
            body={props.error}
          />
        )}
        <div style={{ display: "flex", gap: "var(--gt-sys-space-2)" }}>
          <Button
            variant="primary"
            loading={props.pending}
            disabled={!valid || props.pending}
            onClick={() => props.onSubmit(trimmed)}
            data-testid="gtcc-hw-reject-submit"
          >
            {t("admin.gtcc.hw.reject.dialog.submit")}
          </Button>
          <Button
            variant="secondary"
            disabled={props.pending}
            onClick={props.onClose}
            data-testid="gtcc-hw-reject-cancel"
          >
            {t("admin.gtcc.hw.reject.dialog.cancel")}
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

/* ------------------------------------------------------------------ view */
export function HwView(): ReactNode {
  const { t, locale, direction } = useLocale();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<HwTabId>(initialTabFromQuery);
  const [rejectDraft, setRejectDraft] = useState<RejectDraft | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);

  // D-GTCC-ACCEPT-SHELL1 — the Shell's breadcrumb reads the active tab from
  // `?tab=` (GtccShell.tsx) to show it as the 3rd segment; without the query
  // param a fresh load (no `?tab=` yet) leaves the breadcrumb one level
  // short even though a tab is in fact active. Writing the resolved id once
  // on mount keeps the URL truthful without changing any rendered content
  // (mirrors GtccCockpitView.tsx:654, TenView.tsx, DplaneView.tsx).
  useEffect(() => {
    writeQuery(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pendingQuery = useQuery({
    queryKey: HW_PENDING_KEY,
    queryFn: ({ signal }) => fetchHwPendingDevices(signal),
    ...HW_QUERY_OPTIONS,
  });
  const flaggedQuery = useQuery({
    queryKey: HW_FLAGGED_DEVICES_KEY,
    queryFn: ({ signal }) => fetchHwFlaggedDevices(signal),
    ...HW_QUERY_OPTIONS,
  });
  const inventoryQuery = useQuery({
    queryKey: HW_INVENTORY_KEY,
    queryFn: ({ signal }) => fetchHwInventory(signal),
    ...HW_QUERY_OPTIONS,
  });
  const flowQuery = useQuery({
    queryKey: HW_VEHICLE_FLOW_KEY,
    queryFn: ({ signal }) => fetchHwVehicleFlow(signal),
    ...HW_QUERY_OPTIONS,
  });
  const poolQuery = useQuery({
    queryKey: HW_POOL_KEY,
    queryFn: ({ signal }) => fetchHwPool(signal),
    ...HW_QUERY_OPTIONS,
  });

  const pending: HwPendingDevicesDto | null = dataOf(pendingQuery);
  const flagged: HwFlaggedDevicesDto | null = dataOf(flaggedQuery);
  const inventory: HwInventoryDto | null = dataOf(inventoryQuery);
  const flow: HwVehicleFlowDto | null = dataOf(flowQuery);
  const pool: HwPoolDto | null = dataOf(poolQuery);

  const queries = [pendingQuery, flaggedQuery, inventoryQuery, flowQuery, poolQuery];
  /* Nur `isPending`: eine mit einem Fehler gesettelte Query traegt kein `data`
     — wer sie als „laedt noch" zaehlt, dreht den Spinner ewig. */
  const allPending = queries.every((query) => query.isPending);
  const anyLoaded =
    pending !== null || flagged !== null || inventory !== null || flow !== null || pool !== null;
  const deniedOnly = queries.some(
    (query) => query.data !== undefined && !query.data.ok && query.data.kind === "forbidden",
  );

  const failureMessage = useCallback(
    (result: GtccResult<HwDecisionDto>): string | null => {
      if (result.ok) return null;
      if (result.kind === "disabled") return t("admin.gtcc.hw.failure.disabled");
      if (result.kind === "forbidden") return t("admin.gtcc.hw.failure.forbidden");
      if (result.kind === "upstream") {
        return t("admin.gtcc.hw.failure.upstream", { status: result.httpStatus });
      }
      return t("admin.gtcc.hw.failure.transport");
    },
    [t],
  );

  /* Nach einer Entscheidung ist die Warteschlange von gestern: beide Listen,
     die das Geraet fuehren, werden neu geholt — nichts wird lokal umgebucht. */
  const invalidateQueue = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: HW_PENDING_KEY });
    void queryClient.invalidateQueries({ queryKey: HW_POOL_KEY });
  }, [queryClient]);

  const approveMutation = useMutation({
    mutationFn: (input: { deviceId: string; tenantId: string }) =>
      approveHwDevice(input.deviceId, { tenantId: input.tenantId }),
    onSuccess: (result) => {
      const message = failureMessage(result);
      setWriteError(message);
      if (message !== null) return;
      invalidateQueue();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (input: { deviceId: string; tenantId: string; reason: string }) =>
      rejectHwDevice(input.deviceId, { tenantId: input.tenantId, reason: input.reason }),
    onSuccess: (result) => {
      const message = failureMessage(result);
      setWriteError(message);
      if (message !== null) return;
      setRejectDraft(null);
      invalidateQueue();
    },
  });

  const onApprove = useCallback(
    (device: HwPendingDeviceDto) => {
      /* Ohne Tenant gibt es nichts zu entscheiden; der Knopf ist dann bereits
         gesperrt, und dieser Wächter haelt die Bedingung an einer Stelle. */
      if (device.tenantId === null) return;
      setWriteError(null);
      approveMutation.mutate({ deviceId: device.id, tenantId: device.tenantId });
    },
    [approveMutation],
  );

  const onReject = useCallback((device: HwPendingDeviceDto) => {
    if (device.tenantId === null) return;
    setWriteError(null);
    setRejectDraft({ deviceId: device.id, tenantId: device.tenantId, imei: device.imei });
  }, []);

  const onRejectSubmit = useCallback(
    (reason: string) => {
      if (rejectDraft === null) return;
      rejectMutation.mutate({
        deviceId: rejectDraft.deviceId,
        tenantId: rejectDraft.tenantId,
        reason,
      });
    },
    [rejectDraft, rejectMutation],
  );

  const onTabChange = useCallback((next: string) => {
    if (!isHwTabId(next)) return;
    setActiveTab(next);
    writeQuery(next);
  }, []);

  const freshSeconds = oldestFreshness([flagged, inventory, flow]);

  const head = (
    <ControlHead
      testId="gtcc-hw-head"
      title={t("admin.gtcc.hw.head.title")}
      subtitle={t("admin.gtcc.hw.head.subtitle")}
      fresh={
        <ControlFreshBadge
          testId="gtcc-hw-fresh"
          text={
            freshSeconds === null
              ? t("admin.gtcc.hw.fresh.unmeasurable")
              : t("admin.gtcc.hw.fresh.seconds", {
                  seconds: formatInteger(freshSeconds, locale),
                })
          }
        />
      }
    />
  );

  if (allPending) {
    return (
      <Stack gap={3} data-testid="gtcc-view-hw">
        {head}
        <div data-testid="gtcc-hw-loading">
          <LoadingState label={t("admin.gtcc.hw.loading")} />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded) {
    return (
      <Stack gap={3} data-testid="gtcc-view-hw">
        {head}
        <div data-testid="gtcc-hw-failure">
          <ErrorState
            severity="warn"
            title={t(deniedOnly ? "admin.gtcc.hw.forbidden.title" : "admin.gtcc.hw.failure.title")}
            body={t(deniedOnly ? "admin.gtcc.hw.forbidden.body" : "admin.gtcc.hw.failure.body")}
          />
        </div>
      </Stack>
    );
  }

  const items: ReadonlyArray<TabItem> = HW_TAB_IDS.map((id) => ({
    id,
    label: t(`admin.gtcc.hw.tab.${id}`),
    testId: `${TAB_ID_PREFIX}-tab-${id}`,
  }));

  const busyId =
    approveMutation.isPending && typeof approveMutation.variables?.deviceId === "string"
      ? approveMutation.variables.deviceId
      : null;

  return (
    <Stack gap={3} data-testid="gtcc-view-hw">
      {head}
      <div style={TAB_BAR_STYLE}>
        <Tabs
          items={items}
          value={activeTab}
          onChange={onTabChange}
          ariaLabel={t("admin.gtcc.hw.tabs_aria")}
          idPrefix={TAB_ID_PREFIX}
          direction={direction}
          dataTestId="gtcc-hw-tablist"
        />
      </div>
      <div
        role="tabpanel"
        id={`${TAB_ID_PREFIX}-panel-${activeTab}`}
        aria-labelledby={`${TAB_ID_PREFIX}-tab-${activeTab}`}
        data-testid={`gtcc-hw-panel-${activeTab}`}
      >
        <Stack gap={3}>
          {activeTab === "approvals" ? (
            <>
              <HwApprovalQueuePanel
                pending={asList(pending?.items)}
                truncated={pending?.truncated === true}
                locale={locale}
                busyId={busyId}
                onApprove={onApprove}
                onReject={onReject}
              />
              {writeError === null ? null : (
                <div data-testid="gtcc-hw-write-failure">
                  <ErrorState
                    severity="warn"
                    title={t("admin.gtcc.hw.failure.write_title")}
                    body={writeError}
                  />
                </div>
              )}
              <HwTiles
                flow={flow}
                inventory={inventory}
                pendingCount={pending === null ? null : asList(pending.items).length}
                locale={locale}
              />
              <div className={GRID_MAIN_CLASS}>
                <div style={GRID_COLUMN_STYLE}>
                  <HwFlaggedPanel flagged={flagged} locale={locale} />
                </div>
                <div style={GRID_COLUMN_STYLE}>
                  <HwStockPanel pool={pool} inventory={inventory} locale={locale} />
                </div>
              </div>
            </>
          ) : null}
          {activeTab === "inventory" ? (
            <HwRowsPanel
              testId="gtcc-hw-inventory"
              title={t("admin.gtcc.hw.inventory.title")}
              note={t("admin.gtcc.hw.inventory.note")}
              rows={asList(flow?.rows)}
              locale={locale}
              emptyTitle={t("admin.gtcc.hw.inventory.empty.title")}
              emptyBody={t("admin.gtcc.hw.inventory.empty.body")}
            />
          ) : null}
          {activeTab === "sims" ? <SimsSection /> : null}
          {activeTab === "managed" ? <HardwareSection /> : null}
        </Stack>
      </div>
      <HwRejectDialog
        draft={rejectDraft}
        pending={rejectMutation.isPending}
        error={writeError}
        onClose={() => {
          setRejectDraft(null);
          setWriteError(null);
        }}
        onSubmit={onRejectSubmit}
      />
    </Stack>
  );
}

export default HwView;
